import {
  ExposureProtection,
  ProtectionType,
  Quote as QuoteData,
} from '../types/quote';

const ProtectionTypeArray = Object.values(ProtectionType);
export class Quote {
  quoteData: QuoteData;

  main: QuoteMain;
  details: QuoteDetails;

  constructor(quoteData?: QuoteData) {
    this.quoteData = quoteData;
    if (
      quoteData &&
      quoteData.exposureProtections.some(
        (exposureProtection) =>
          !ProtectionTypeArray.includes(exposureProtection.name)
      )
    ) {
      throw 'Unsupported protection type';
    }
    this.main = new QuoteMain(quoteData);
    this.details = new QuoteDetails(quoteData);
  }
}

export class QuoteMain {
  title: string = `Protect Your Trip from Weather`;
  suggested_price: string;
  suggested_price_total: string;
  body: string = `Sensible Weather will automatically reimburse you when Mother Nature has other plans for your day - No Claims, No Hassle.`;
  action: string = `Add Weather Guarantee`;
  open_details: string = `Learn More`;
  terms_conditions: string = `We believe in no surprises, read our Terms & Conditions`;
  privacy: string = `View our Privacy Policy`;

  constructor(quoteData?: QuoteData) {
    if (!quoteData) {
      this.suggested_price = `Add a Weather Guarantee for `;
      return;
    }

    if (quoteData.pricePerDay === quoteData.totalPrice) {
      this.suggested_price = `Add a Weather Guarantee for $${quoteData.totalPrice.toFixed(
        2
      )}`;
      this.suggested_price_total = ``;
    } else {
      this.suggested_price = `Add a Weather Guarantee for $${quoteData.pricePerDay.toFixed(
        2
      )}/day`;
      this.suggested_price_total = `($${quoteData.totalPrice.toFixed(
        2
      )} total)`;
    }
  }
}

export class QuoteDetails {
  title: string = `Protect Your Adventure with Sensible Weather`;
  summary_heading: string = `Included in Your Coverage:`;
  summary_times: string;
  summary_tiers: string[] = [];
  summary_subheading: string = `How's the coverage triggered?`;
  summary_body: string;
  summary_body_alt_unit: string;
  summary_explainer: string;
  steps_heading: string = `What to Expect from Sensible Weather:`;
  step_1_heading: string = `Watching the Forecast`;
  step_1_body: string = `Sensible Weather diligently tracks the NOAA forecast during your trip.`;
  step_2_heading: string = `Keeping you Updated`;
  step_2_body: string = `Bad weather forecasted?  Sensible will text when your reimbursement is ready.`;
  step_3_heading: string = `Money Back, Promptly`;
  step_3_body: string = `Once reimbursed, simply transfer funds to the account of your choosing.`;
  suggested_price: string;
  suggested_price_total: string;
  action: string = `Add`;
  close: string = `Close Window`;
  terms_conditions: string = `We believe in no surprises, read our Terms & Conditions`;
  privacy: string = `View our Privacy Policy`;
  constructor(quoteData: QuoteData) {
    if (!quoteData) return;
    const exposureProtection = quoteData.exposureProtections[0];
    this.summary_times = `Forecasted ${exposureProtectionNoun(
      exposureProtection
    )} between ${formatTime(quoteData.dailyCoverageStartHour)} and ${formatTime(
      quoteData.dailyCoverageEndHour
    )} each day.`;
    this.summary_tiers = quoteData.sortedTiers.map((tier, index) => {
      const hours =
        index < quoteData.sortedTiers.length - 1
          ? `${tier.hours} - ${quoteData.sortedTiers[index + 1].hours}`
          : `${tier.hours}+`;
      return `${hours} hours of ${exposureProtectionNoun(
        exposureProtection
      )} = $${tier.reimbursementRate.toFixed(2)} in your pocket`;
    });

    const primaryValueString = exposureProtectionToString(
      quoteData.exposureProtections[0]
    );
    this.summary_body = createSummaryBody(quoteData, primaryValueString);

    const altValueString = exposureProtectionToAltString(
      quoteData.exposureProtections[0]
    );
    this.summary_body_alt_unit = createSummaryBody(quoteData, altValueString);

    this.summary_explainer = createSummaryExplainer(exposureProtection);

    if (quoteData.pricePerDay === quoteData.totalPrice) {
      this.suggested_price = `Add a Weather Guarantee for $${quoteData.totalPrice.toFixed(
        2
      )}`;
      this.suggested_price_total = ``;
    } else {
      this.suggested_price = `Add a Weather Guarantee for $${quoteData.pricePerDay.toFixed(
        2
      )}/day`;
      this.suggested_price_total = `$${quoteData.totalPrice.toFixed(2)} total`;
    }
  }
}

const createSummaryBody = (
  quoteData: QuoteData,
  protectionValueString: string
) => {
  const exposureProtection = quoteData.exposureProtections[0];
  return `A forecasted "${exposureProtectionAdjective(
    exposureProtection
  )}${exposureProtectionNoun(
    exposureProtection
  )}" of ${protectionValueString}, or ${exposureProtectionToExcessString(
    exposureProtection
  )}, for ${
    quoteData.sortedTiers[quoteData.sortedTiers.length - 1].hours
  }+ hours will trigger the daily coverage.  We'll alert you if bad weather is on the horizon.  Don't worry, we'll only text you if it's important—aka you have money available.`;
};

const createSummaryExplainer = (
  exposureProtection: ExposureProtection
): string => {
  switch (exposureProtection.name) {
    case ProtectionType.RAINFALL:
      return `Light rain is when you walk outside, and the ground looks like it's been raining, think small puddles.`;
    case ProtectionType.WIND:
      return `For high wind, you don't have to feel like you're in a hurricane.  It's more of a light breeze.  You can feel it, and your hair might be blown back but it's not a wind tunnel.';`;
    case ProtectionType.TEMPERATURE:
      if (exposureProtection.upperThreshold) {
        return `High temperature is a bit of heat.  It's shorts and shirt kind of day, you might have a light sweat but you're not melting.`;
      } else {
        return `Low temperature is light jacket weather.  It's chilly, but you don't feel frozen to the bone.`;
      }
  }
};

const fahrenheitToCelsius = (fahrenheit: number): number =>
  (fahrenheit - 32) / 1.8;
const celsiusToFahrenheit = (celsius: number): number => celsius * 1.8 + 32;
const mpsToKph = (mps: number): number => mps * 3.6;
const mpsToMph = (mps: number): number => mps * 2.236936;
const meterToMillimeter = (meters: number) => meters * 1000;

const exposureProtectionToString = (
  exposureProtection: ExposureProtection
): string => {
  switch (exposureProtection.name) {
    case ProtectionType.RAINFALL:
      return `${meterToMillimeter(
        exposureProtection.upperThreshold
      )} mm per hour`;
    case ProtectionType.SNOW:
      return `${exposureProtection.upperThreshold}"`;
    case ProtectionType.TEMPERATURE:
      const exposureThreshold =
        exposureProtection.upperThreshold || exposureProtection.lowerThreshold;
      const exposureCelsius =
        exposureProtection.unit === 'celsius'
          ? exposureThreshold
          : fahrenheitToCelsius(exposureThreshold).toFixed(0);
      return `${exposureCelsius}° C`;
    case ProtectionType.WIND:
      const exposureKmh = mpsToKph(exposureProtection.upperThreshold).toFixed(
        0
      );
      return `${exposureKmh} km/h`;
    case ProtectionType.AIR_QUALITY:
      return `${exposureProtection.upperThreshold} ppm`;
  }
};

const exposureProtectionToAltString = (
  exposureProtection: ExposureProtection
): string => {
  switch (exposureProtection.name) {
    case ProtectionType.RAINFALL:
      return `${exposureProtection.upperThreshold} meters per hour`;
    case ProtectionType.SNOW:
      return `${exposureProtection.upperThreshold}"`;
    case ProtectionType.TEMPERATURE:
      const exposureThreshold =
        exposureProtection.upperThreshold || exposureProtection.lowerThreshold;
      const exposureFahrenheit =
        exposureProtection.unit === 'fahrenheit'
          ? exposureThreshold
          : celsiusToFahrenheit(exposureThreshold).toFixed(0);
      return `${exposureFahrenheit}° F`;
    case ProtectionType.WIND:
      const exposureMph = mpsToMph(exposureProtection.upperThreshold).toFixed(
        0
      );
      return `${exposureMph} mph`;
    case ProtectionType.AIR_QUALITY:
      return `${exposureProtection.upperThreshold} ppm`;
  }
};

const exposureProtectionToExcessString = (
  exposureProtection: ExposureProtection
): string => {
  switch (exposureProtection.name) {
    case ProtectionType.RAINFALL:
    case ProtectionType.SNOW:
    case ProtectionType.WIND:
      return 'more';
    case ProtectionType.TEMPERATURE:
      return exposureProtection.upperThreshold === undefined
        ? 'lower'
        : 'higher';
    case ProtectionType.AIR_QUALITY:
      return 'worse';
  }
};

const exposureProtectionAdjective = (
  exposureProtection: ExposureProtection
): string => {
  switch (exposureProtection.name) {
    case ProtectionType.RAINFALL:
      return 'light ';
    case ProtectionType.SNOW:
      return 'heavy ';
    case ProtectionType.WIND:
      return 'high ';
    case ProtectionType.TEMPERATURE:
      return '';
  }
};

const exposureProtectionNoun = (
  exposureProtection: ExposureProtection
): string => {
  switch (exposureProtection.name) {
    case ProtectionType.RAINFALL:
      return 'rain';
    case ProtectionType.SNOW:
      return 'snow';
    case ProtectionType.WIND:
      return 'wind';
    case ProtectionType.TEMPERATURE:
      if (exposureProtection.upperThreshold) {
        return 'high temperature';
      } else {
        return 'low temperature';
      }
    case ProtectionType.AIR_QUALITY:
      return 'low air quality';
  }
};

const formatTime = (time: number): string => {
  if (time === 0 || time === 24) return 'midnight';
  if (time === 12) return 'noon';
  if (time < 12) return `${time}am`;
  return `${time % 12}pm`;
};
